import z from 'zod'

import { ClientTypeOptions, PriorityClient, RepeatReminder } from '@constants/client'

import { UserSchema } from './user'
import { EventBaseSchema } from './event'
import { GetEntitySchema } from './network'
import { DocumentReference } from 'firebase/firestore'

export const PrioritySchema = z.custom<keyof typeof PriorityClient>(
  (value) => Object.keys(PriorityClient).includes(value),
  { message: 'Prioridad inválida' },
)

export type PriorityType = z.infer<typeof PrioritySchema>

export const RepeatReminderSchema = z.custom<keyof typeof RepeatReminder>(
  (value) => Object.keys(RepeatReminder).includes(value),
  { message: 'Recordatorio inválido' },
)

export const ClientTypeSchema = z.custom<keyof typeof ClientTypeOptions>(
  (value) => Object.keys(ClientTypeOptions).includes(value),
  { message: 'Tipo de cliente inválido' },
)

export type ClientTypeType = z.infer<typeof ClientTypeSchema>

export type RepeatReminderType = z.infer<typeof RepeatReminderSchema>

export const StatusClientSchema = z.object({
  uid: z.string(),
  name: z
    .string()
    .min(1, 'Estado debe tener al menos 1 caracter')
    .max(50, 'Estado debe tener como máximo 50 caracteres')
    .trim(),
  color: z
    .string()
    .min(4, 'Color debe tener al menos 4 caracteres')
    .max(7, 'Color debe tener como máximo 7 caracteres')
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Color debe ser un código hexadecimal válido'),
  order: z.number().positive('Orden debe ser un número positivo'),
  createdAt: z.number(),
  updatedAt: z.number(),
})

export type StatusClientType = z.infer<typeof StatusClientSchema>

export const ContactBaseSchema = z.object({
  uid: z.string(),
  name: z
    .string()
    .min(3, 'Nombre debe tener al menos 3 caracteres')
    .max(50, 'Nombre debe tener como máximo 50 caracteres')
    .trim(),
  email: z.string().email('Correo electrónico no válido').optional(),
  phone: z
    .string()
    .min(6, 'Teléfono debe tener al menos 6 caracteres')
    .max(10, 'Teléfono debe tener como máximo 10 caracteres')
    .trim()
    .optional(),
  master: z.boolean().optional().nullable(),
  updatedAt: z.number(),
})

export type ContactBaseType = z.infer<typeof ContactBaseSchema>

export const CreateContactSchema = ContactBaseSchema.omit({ updatedAt: true })

export type CreateContactType = z.infer<typeof CreateContactSchema>

export const ClientBaseSchema = z.object({
  uid: z.string(),
  name: z
    .string()
    .min(3, 'Nombre debe tener al menos 3 caracteres')
    .max(50, 'Nombre debe tener como máximo 50 caracteres')
    .trim(),
  description: z.string().max(200, 'Descripción debe tener como máximo 200 caracteres').trim(),
  priority: PrioritySchema,
  //remainder: z.array(z.number()),
  contacts: z.array(ContactBaseSchema).max(4, 'No puede tener más de 3 contactos'),
  updatedAt: z.number(),
  createdAt: z.number(),
  // Fields contact company level (used for proforma)
  email: z.string().email('Correo electrónico no válido').optional(),
  phone: z.string().max(10, 'Teléfono debe tener como máximo 10 caracteres').trim().optional(),
  ruc: z.string().max(14, 'RUC debe tener como máximo 14 caracteres').trim().optional(),
  location: z
    .string()
    .max(100, 'Ubicación debe tener como máximo 100 caracteres')
    .trim()
    .optional(),
  status: StatusClientSchema.nullish(),
  type: ClientTypeSchema,
})

export type ClientBaseType = z.infer<typeof ClientBaseSchema>

export const ClientSchema = ClientBaseSchema.extend({
  events: z.array(z.lazy(() => EventBaseSchema)),
})

export type ClientType = z.infer<typeof ClientSchema>

// Status update
export const StatusUpdateClientSchema = z.object({
  uid: z.string(),
  clientId: z.string(),
  status: StatusClientSchema.pick({
    uid: true,
    name: true,
    color: true,
  }),
  updateBy: z.lazy(() => UserSchema),
  updatedAt: z.number(),
})

export type StatusUpdateClientType = z.infer<typeof StatusUpdateClientSchema>

// Internal use
export const ClientFirebaseSchema = ClientSchema.omit({ events: true, status: true }).extend({
  events: z.array(z.custom<DocumentReference>()),
  status: z.custom<DocumentReference>().nullish(),
})

export type ClientFirebaseType = z.infer<typeof ClientFirebaseSchema>

// Create
export const CreateClientSchema = ClientBaseSchema.omit({
  uid: true,
  updatedAt: true,
  createdAt: true,
})

export type CreateClientType = z.infer<typeof CreateClientSchema>

export const CreateStatusClientSchema = StatusClientSchema.omit({
  uid: true,
  createdAt: true,
  updatedAt: true,
})

export type CreateStatusClientType = z.infer<typeof CreateStatusClientSchema>

// Update
export const UpdateClientSchema = ClientBaseSchema.omit({ createdAt: true, updatedAt: true })
  .partial()
  .extend({
    uid: z.string(),
  })

export type UpdateClientType = z.infer<typeof UpdateClientSchema>

export const UpdateStatusClientSchema = z.object({
  uid: z.string(),
  data: CreateStatusClientSchema,
})

export type UpdateStatusClientType = z.infer<typeof UpdateStatusClientSchema>

// Get opportunities
export const GetClientsSchema = GetEntitySchema

export type GetClientsType = z.infer<typeof GetClientsSchema>
